import { useEffect, useRef, useState } from 'react';
import { EntityTable, HeadCell } from '@mb.io/core-components';
import { WarningPanel } from '@backstage/core-components';
import { EntityCardsView } from '../EntityCardsView/EntityCardsView';
import Box from '@mui/material/Box';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { makeStyles } from '@mui/styles';
import { useLocation } from 'react-router-dom';
import { useCatalogContext } from '../../context/CatalogContext';
import { useCatalogEntitiesDetail } from '../../hooks/useCatalogEntitiesDetail';
import LinearProgress from '@mui/material/LinearProgress';
import { EmptyResultViewWrapper } from '../EmptyResultViewWrapper/EmptyResultViewWrapper';
import { allowedEntitiesPerPage } from '../../constants';

const HEAD_CELLS = [
  { id: 'name', label: 'Name' },
  { id: 'kind', label: 'Kind', width: '140px' },
  { id: 'type', label: 'Type', width: '120px' },
  { id: 'owner', label: 'Owner' },
  { id: 'tags', label: 'Tags' },
  { id: 'description', label: '', width: '50px' },
] as HeadCell[];

const useStyles = makeStyles(() => ({
  wrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'start',
    marginBottom: '32px',
    gap: '16px',
    width: '100%',
  },
  messageAndSwitch: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  boxMessage: {
    marginTop: '32px',
    height: '20px',
  },
  formGroup: {
    display: 'flex',
    marginTop: '20px',
  },
  formControlLabel: {
    whiteSpace: 'nowrap',
  },
}));

export const EntityView = () => {
  const isFirstLoad = useRef(true);
  const { isListView, setIsListView } = useCatalogContext();
  const [displayMessage, setDisplayMessage] = useState<string>('');

  const {
    totalItems = 0,
    limit,
    page,
    loading,
    error,
    formattedItems,
    offset = 0,
    setLimit,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useCatalogEntitiesDetail();

  const location = useLocation();

  const classes = useStyles();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const limitString = searchParams.get('limit');
    const limitNumber = parseInt(limitString ?? '12', 10) || 12;

    const isLimitAllowed = allowedEntitiesPerPage.includes(limitNumber);

    if (isFirstLoad.current) {
      setLimit(isLimitAllowed ? limitNumber : allowedEntitiesPerPage[0]);
      isFirstLoad.current = false;
    }
  }, [limit, totalItems, location.search, setLimit]);

  useEffect(() => {
    const getViewMessage = (
      numberEntities: number,
      numberTotalItems: number,
    ) => {
      // Ensure rowsPerPage is not greater than totalItems
      const validRowsPerPage = Math.min(numberEntities, numberTotalItems);

      if (validRowsPerPage === 0 || numberTotalItems === 0) {
        setDisplayMessage('');
        return;
      }

      const remainingItems = numberTotalItems - offset;
      const itemsOnCurrentPage = Math.min(validRowsPerPage, remainingItems);
      setDisplayMessage(
        `Viewing ${itemsOnCurrentPage} of ${numberTotalItems} results`,
      );
    };

    getViewMessage(limit, totalItems);
  }, [
    setDisplayMessage,
    isListView,
    limit,
    offset,
    formattedItems,
    totalItems,
  ]);

  if (error) {
    return (
      <WarningPanel
        severity="error"
        title="Could not load Filters for Entity table."
      >
        {error.message}
      </WarningPanel>
    );
  }

  const renderEntityView = () => {
    if (isListView) {
      return (
        <EntityTable
          headCells={HEAD_CELLS}
          data-testid="entity-table"
          formattedItems={formattedItems}
          totalItems={totalItems}
          limit={limit}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          allowedEntitiesPerPage={allowedEntitiesPerPage}
        />
      );
    }

    return (
      <EntityCardsView
        cardsPerPage={limit}
        data-testid="entity-cards-view"
        formattedItems={formattedItems}
        totalItems={totalItems}
        page={page}
        handleChangePage={handleChangePage}
      />
    );
  };

  return (
    <>
      {loading && <LinearProgress sx={{ marginTop: '48px' }} />}
      {!loading && <EmptyResultViewWrapper />}
      {!loading && totalItems > 0 && (
        <>
          <Box className={classes.messageAndSwitch}>
            <Box className={classes.boxMessage}>{displayMessage}</Box>
            <FormGroup className={classes.formGroup}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isListView}
                    id="list-view-toggle"
                    data-testid="list-view-toggle"
                    onChange={() => setIsListView(!isListView)}
                  />
                }
                label="List View"
                className={classes.formControlLabel}
              />
            </FormGroup>
          </Box>
          <div className={classes.wrapper}>{renderEntityView()}</div>
        </>
      )}
    </>
  );
};
